import { yupResolver } from '@hookform/resolvers/yup'
import { FC } from 'react'
import { isMobileOnly } from 'react-device-detect'
import { useForm } from 'react-hook-form'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import { IFilterParam } from 'types/TableServerRequest'

import { showNotification } from 'utils/showNotification'

import { useRebindProxiesMutation, useGetCountriesQuery } from 'services/bots/api'

import { REBIND_PROXIES_SCHEMA } from 'components/bots/bots/RebindProxiesModal/validation'
import { AppModal } from 'components/common/AppModal/index'
import Button from 'components/common/Button/index'
import CustomCheckbox from 'components/common/Checkbox/index'
import { ControlledSelectAutocomplete } from 'components/common/CustomSelectAutocomplete/ControlledSelectAutocomplete/index'
import TextInput from 'components/common/TextInput/index'

interface Props {
  onClose: () => void
  totalElements: number
  selectedBotIds: number[]
  paginationState: {
    page: {
      size: number
      pageNumber: number
    }
    filters: IFilterParam[]
    orderBy: string
    direction: 'asc' | 'desc'
    quickSearch: string
  }
}

interface FormValues {
  isAllBots: boolean

  proxyTags: string
  ignoreOriginalCountries: boolean
  countryIds?: {
    value: string
    name: string
  }[]
}

export const RebindProxiesModal: FC<Props> = ({
  onClose,
  totalElements,
  selectedBotIds,
  paginationState,
}) => {
  const { data: countries } = useGetCountriesQuery()
  const COUNTRIES_OPTIONS = countries?.filter((country) => country.name !== 'Any') || []
  const [rebindProxies, { isLoading: isRebinding }] = useRebindProxiesMutation()
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    mode: 'onBlur',
    resolver: yupResolver(REBIND_PROXIES_SCHEMA),
    defaultValues: {
      isAllBots: selectedBotIds.length === 0,
      proxyTags: '',
      ignoreOriginalCountries: false,
      countryIds: [],
    },
  })

  const onSubmit = async ({
    proxyTags,
    ignoreOriginalCountries,
    countryIds,
    isAllBots,
  }: FormValues) => {
    await rebindProxies({
      ap: isAllBots ? paginationState : null,
      proxyTags: proxyTags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag),
      ignoreOriginalCountries: ignoreOriginalCountries,
      countryIds: countryIds && countryIds.length > 0 ? countryIds.map((c) => +c.value) : null,
      botIds: isAllBots ? null : selectedBotIds,
    })
      .unwrap()
      .then(() => {
        showNotification('Proxies successfully rebound', 'success')
        onClose()
      })
      .catch(() => showNotification('Error. Try again later', 'error'))
  }

  const botsCount = watch('isAllBots') ? totalElements : selectedBotIds.length

  return (
    <AppModal
      title={`Rebind Proxies for ${botsCount} bot${botsCount > 1 ? 's' : ''}`}
      onClose={onClose}
      loading={isRebinding}
    >
      <AppModal.Wrapper
        component="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        sx={{ width: '600px', maxHeight: 'calc(100dvh - 250px)', height: 'fit-content' }}
      >
        <AppModal.ContentBox
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CustomCheckbox
            control={control}
            name="isAllBots"
            label={`Select all bots that match the current filter (${totalElements} bots)`}
          />

          <TextInput
            control={control}
            name="proxyTags"
            label="New proxy tags (separated by comma)"
            errors={errors}
            helperText="Enter tags separated by comma"
          />

          <CustomCheckbox
            control={control}
            name="ignoreOriginalCountries"
            label="Ignore original countries"
          />

          <Box>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Use only:
            </Typography>
            <ControlledSelectAutocomplete
              name="countryIds"
              label="Countries"
              errors={errors?.countryIds}
              control={control}
              selectOptions={COUNTRIES_OPTIONS}
              multiple
              disableClearable={false}
              fullWidth
            />
          </Box>
        </AppModal.ContentBox>
        <AppModal.ButtonsBox>
          <Button variant="outlined" color="error" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isRebinding || botsCount === 0}
            fullWidth={isMobileOnly}
          >
            Rebind Proxies
          </Button>
        </AppModal.ButtonsBox>
      </AppModal.Wrapper>
    </AppModal>
  )
}
